import {get, isEmpty, isNil} from 'lodash'
import themeVars from 'theme/themeVars'
import {PartType, updatePartPrice} from 'helpers/parts'
import {getPartPackFor, isNonCumulativePricingMethod} from 'helpers/packs'

export const nullPriceBreak = {
  cost: null,
  createdDate: null,
  fixedPrice: null,
  id: null,
  markup: null,
  maxPacks: null,
  minPacks: null,
  modifiedDate: null,
  partPackId: null,
  salesDiscount: null,
  guid: null,
}

export const getPartPriceBreaks = (partPack, isComplexPricingEnabled = false, companyId = null) => {
  const partPriceBreaks = getPriceBreaksForCompany(partPack, companyId)

  if (!isComplexPricingEnabled) {
    const minPartPriceBreak = getMinPriceBreak(partPriceBreaks)
    return [minPartPriceBreak]
  }

  return partPriceBreaks
}

export const getPriceBreaksForCompany = (partPack, companyId) => {
  const partPriceBreaks = get(partPack, 'prices', []) || []
  const globalPriceBreaks = partPriceBreaks.filter(priceBreak => isNil(get(priceBreak, 'companyId', null)))
  const companyPriceBreaks = isNil(companyId)
    ? []
    : partPriceBreaks.filter(priceBreak => get(priceBreak, 'companyId') === companyId)
  return isEmpty(companyPriceBreaks) ? globalPriceBreaks : companyPriceBreaks
}

export const getMinPriceBreak = (prices = []) => {
  return isNil(prices) || prices.length === 0
    ? nullPriceBreak
    : prices.reduce((a, b) => a.minPacks < b.minPacks ? a : b)
}

export const getMaxPriceBreak = (prices = []) => {
  return isNil(prices) || prices.length === 0
    ? nullPriceBreak
    : prices.reduce((a, b) => a.minPacks > b.minPacks ? a : b)
}

export const updatePartPriceBreak = (part, partPriceBreak, key, value) => {
  partPriceBreak[key] = value
  updatePartPrice(part, partPriceBreak, key)
  return partPriceBreak
}

export const updateAllPartPriceBreaksFor = (partPackId, partPriceBreaks, part) => {
  let partPacks = get(part, 'packs', [])
  partPacks = partPacks.map(partPack => {
    if (partPack.id === partPackId) {
      partPack['prices'] = partPriceBreaks
      return partPack
    }
    return partPack
  })

  part['packs'] = partPacks
  return part
}

export const updatePartPriceBreaksFor = (partPackId, partPriceBreakId, key, value, part) => {
  let partPacks = get(part, 'packs', [])
  partPacks = partPacks.map(partPack => {
    if (partPack.id === partPackId) {
      let prices = get(partPack, 'prices', [])
      prices = prices.map(partPriceBreak => {
        return (partPriceBreak.id === partPriceBreakId)
          ? updatePartPriceBreak(part, partPriceBreak, key, value)
          : partPriceBreak
      })
      partPack['prices'] = prices
      return partPack
    }
    return partPack
  })

  part['packs'] = partPacks

  return part
}

export const updatePartPriceBreaksWith = (updatedPartPriceBreak, partPackId, partPriceBreakId, part) => {
  let partPacks = get(part, 'packs', [])
  partPacks = partPacks.map(partPack => {
    if (partPack.id === partPackId) {
      let prices = get(partPack, 'prices', [])
      prices = prices.map(partPriceBreak => {
        return (partPriceBreak.id === partPriceBreakId)
          ? updatedPartPriceBreak
          : partPriceBreak
      })
      partPack['prices'] = prices
      return partPack
    }
    return partPack
  })

  part['packs'] = partPacks

  return part
}

export const addPartPriceBreakToPartPack = (part, partPackId, partPriceBreak) => {
  let partPacks = get(part, 'packs', [])
  partPacks = partPacks.map(partPack => {
    if (partPack.id === partPackId) {
      let prices = get(partPack, 'prices', [])
      prices = isNil(prices) ? [] : prices
      prices.push(partPriceBreak)

      let previousPartPriceBreak = getPreviousPartPriceBreak(partPriceBreak, prices)
      if (isNil(previousPartPriceBreak)) {
        partPack['prices'] = prices
      } else {
        previousPartPriceBreak = setMinPacksToMaxPacks(previousPartPriceBreak)
        const previousPartPriceBreakIndex = prices.findIndex(price => price.id === previousPartPriceBreak.id)
        prices[previousPartPriceBreakIndex] = previousPartPriceBreak
        partPack['prices'] = prices
      }

      return partPack
    } else {
      return partPack
    }
  })

  part['packs'] = partPacks
  return part
}

export const deletePartPriceBreakFromPartPack = (part, packTypeId, partPriceBreakId) => {
  let partPacks = get(part, 'packs', [])
  partPacks = partPacks.map(partPack => {
    if (partPack.id === packTypeId) {
      const partPriceBreaks = get(partPack, 'prices', [])

      partPack['prices'] = partPriceBreaks
        .slice()
        .filter(partPriceBreak => partPriceBreak.id !== partPriceBreakId)

      const deletedPartPriceBreak = partPriceBreaks
        .slice()
        .find(partPriceBreak => partPriceBreak.id === partPriceBreakId)

      partPack['deletedPrices'] = [deletedPartPriceBreak]

      return partPack
    } else {
      return partPack
    }
  })

  part['packs'] = partPacks

  return part
}

export const isUseCostBreaksFieldEnabled = (part, partPackId) => {
  const partPacks = get(part, 'packs', [])
  if (isNil(partPacks)) return false

  const partPack = partPacks.find(partPack => partPack.id === partPackId)
  if (isNil(partPack)) return false

  const prices = get(partPack, 'prices', [])
  return isNil(prices)
    ? false
    : prices.some(partPriceBreak => !isNil(partPriceBreak.cost))
}

export const getPartPriceBreakMinPacks = (prices = []) => {
  if (isNil(prices) || prices.length === 0) return 1
  const lastPartPriceBreak = prices[prices.length - 1]
  return lastPartPriceBreak.maxPacks === 0
    ? lastPartPriceBreak.minPacks + 1
    : lastPartPriceBreak.maxPacks + 1
}

export const getPartPriceBreaksFor = (partPackId, part, isComplexPricingEnabled, companyId) => {
  const partPacks = get(part, 'packs', [])
  const partPack = getPartPackFor(partPackId, partPacks)
  return getPartPriceBreaks(partPack, isComplexPricingEnabled, companyId)
}

export const createNewPartPriceBreak = (part, partPackId, partCategory, isComplexPricingEnabled) => {
  const partPriceBreaks = getPartPriceBreaksFor(partPackId, part, isComplexPricingEnabled).slice()
  const minPacks = getPartPriceBreakMinPacks(partPriceBreaks)
  const markup = get(partCategory, 'markup', null)
  const lastPartPriceBreak = partPriceBreaks[partPriceBreaks.length - 1]
  const fixedPrice = get(lastPartPriceBreak, 'fixedPrice', null)
  return {
    id: -1,
    partPackId: partPackId,
    minPacks,
    maxPacks: 0,
    markup,
    salesDiscount: null,
    fixedPrice,
    cost: null,
  }
}

export const isDeletePartPriceBreakEnabled = (part, partPackId, isComplexPricingEnabled, companyId) => {
  if (part.type === PartType.ASSEMBLY.id) return true
  return getPartPriceBreaksFor(partPackId, part, isComplexPricingEnabled, companyId).length > 1
}

export const setMinPacksToMaxPacks = (partPriceBreak) => {
  if (partPriceBreak.maxPacks !== 0) return partPriceBreak
  partPriceBreak['maxPacks'] = partPriceBreak.minPacks
  return partPriceBreak
}

export const getPreviousPartPriceBreak = (partPriceBreak, prices = []) => {
  prices = isNil(prices) ? [] : prices
  const partPriceBreakIndex = prices.findIndex(price => price.id === partPriceBreak.id)
  const previousPartPriceBreakIndex = partPriceBreakIndex - 1
  return previousPartPriceBreakIndex < 0
    ? null
    : prices[previousPartPriceBreakIndex]
}

export const isPriceBreakColumnCalculated = (partPriceBreak, key) => {
  return isNil(get(partPriceBreak, key, null))
}

export const getCalculatedClassName = (partPriceBreak, key) => {
  return isPriceBreakColumnCalculated(partPriceBreak, key)
    ? ' calculated'
    : ''
}

export const getCostColumnStyle = (partPriceBreak) => {
  return isPriceBreakColumnCalculated(partPriceBreak, 'cost')
    ? {color: themeVars.palette.goodColor}
    : {color: themeVars.palette.primaryFontColor}
}

export const getMarkupColumnStyle = (partPack, partPriceBreak) => {
  return isPriceBreakColumnCalculated(partPriceBreak, 'markup')
    ? {color: themeVars.palette.goodColor}
    : {color: themeVars.palette.primaryFontColor}
}

export const getSalesDiscountColumnStyle = (partPriceBreak) => {
  return isPriceBreakColumnCalculated(partPriceBreak, 'salesDiscount')
    ? {color: themeVars.palette.goodColor}
    : {color: themeVars.palette.primaryFontColor}
}

export const getFixedPriceColumnStyle = (partPriceBreak) => {
  return isPriceBreakColumnCalculated(partPriceBreak, 'fixedPrice')
    ? {color: themeVars.palette.goodColor}
    : {color: themeVars.palette.primaryFontColor}
}

export const isPartPriceBreakSequential = (partPriceBreak, prices = []) => {
  const previousPartPriceBreak = getPreviousPartPriceBreak(partPriceBreak, prices)
  if (isNil(previousPartPriceBreak)) return true
  if (previousPartPriceBreak.minPacks >= partPriceBreak.minPacks) return false
  return previousPartPriceBreak.maxPacks < partPriceBreak.minPacks
}

export const isPartPriceBreaksSequential = (prices = []) => {
  return prices
    .every(partPriceBreak => isPartPriceBreakSequential(partPriceBreak, prices))
}

export const arePriceBreaksContinuous = (priceBreaks) => isEmpty(priceBreaks) || priceBreaks
  .sort((a, b) => a.minPacks < b.minPacks ? -1 : 1)
  .every((curr, idx, src) => idx === 0 || src[idx - 1].maxPacks === curr.minPacks - 1)

export const mapPartPriceBreakInPartPriceBreaks = (partPriceBreak, prices = []) => {
  return prices.map(price => price.id === partPriceBreak.id
    ? partPriceBreak
    : price)
}

export const getCumulativePriceBreakValue = (value, partPriceBreaks, currentPartPriceBreak, key) => {
  return isPartPriceBreaksSequential(partPriceBreaks)
    ? value
    : currentPartPriceBreak[key]
}

export const updatePartPriceBreakPacksFor = (key, entity, value, prices = [], pricingMethod) => {
  const partPriceBreak = {...entity}
  partPriceBreak[key] = value
  const currentPartPriceBreak = prices.find(price => price.id === entity.id)
  const partPriceBreaks = mapPartPriceBreakInPartPriceBreaks(partPriceBreak, prices)

  entity[key] = isNonCumulativePricingMethod(pricingMethod)
    ? value
    : getCumulativePriceBreakValue(value, partPriceBreaks, currentPartPriceBreak, key)
}

export const getValidMaxPriceBreak = (value, priceBreak) =>
  value === 0
    ? value
    : Math.max(priceBreak.minPacks ?? 0, value, 0)

export const getQuantityLabel = (price) => {
  if (price.maxPacks === 0)
    return `${price.minPacks}+`

  return price.minPacks === price.maxPacks
    ? `${price.minPacks}`
    : `${price.minPacks} - ${price.maxPacks}`
}
